import { AppShell, Burger, Button, Divider, Flex, Grid, ScrollArea, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useCalculators } from '../../hooks/use-calculators';
import { useDarkMode } from '../../hooks/useDarkMode';
import { hasNpmPackage } from '../../versions/npm-package';
import { hasPreV4Pages } from '../../versions/pre-v4-pages';
import { usesSplitOpenApiSpecs } from '../../versions/split-openapi';
import { useVersion } from '../../versions/url-version';
import { docSlug } from '../../versions/versions';
import { VISIBLE_VERSIONS } from '../../versions/visible-versions';
import RouterNavLink from '../RouterNavLink';
import exogeeBlack from './exogee-black.svg';
import exogeeWhite from './exogee-white.svg';
import './Layout.css';
import aiaLogo from './logo-light.svg';

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { version: urlSegment } = useParams();

  const currentVersion = useVersion();
  const currentSlug = docSlug(currentVersion);
  const { calculators } = useCalculators(usesSplitOpenApiSpecs(currentVersion) ? currentVersion : undefined);

  // Stay on the same page in the new version, the router falls back to the version root if the page doesn't exist
  const handleVersionChange = (newVersion: string) => {
    const pathname = location.pathname.replace(/^\/api\/[^/]+/, `/api/${docSlug(newVersion)}`);
    navigate({ pathname, search: location.search, hash: location.hash });
  };

  const [opened, { toggle }] = useDisclosure();
  const isDarkMode = useDarkMode();

  // useVersion falls back to the max docs version for unknown or hidden versions, so send those URLs there too
  if (urlSegment !== currentSlug) {
    return <Navigate to={`/api/${currentSlug}`} replace />;
  }

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 300, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      footer={{ height: 60 }}
      padding="md"
    >
      <AppShell.Header>
        <Flex h="100%">
          <Flex justify="left" align="center" miw="300px">
            <Title order={1} className="logo" pt="sm">
              <Link to="/">
                <img src={aiaLogo} alt="AIA Logo" className="logo-image" />
              </Link>
            </Title>
          </Flex>
          <Grid w="100%" h="100%" m="0" classNames={{ inner: 'grid-inner' }}>
            <Grid.Col span={9}>
              <Flex gap="md" justify="flex-end" align="center" h="100%">
                <Button variant="light" component="a" href="mailto:contact@aginnovationaustralia.com.au">
                  Email us
                </Button>
                <Button variant="outline" component="a" href="https://aginnovationaustralia.com.au/contact-us/">
                  Contact us
                </Button>
              </Flex>
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            </Grid.Col>
          </Grid>
        </Flex>
      </AppShell.Header>
      <AppShell.Navbar className="navbar">
        <ScrollArea>
          <Flex direction="column" justify="space-between" gap="md" m="md">
            <div className="version-selector">
              <label htmlFor="version-select">Version:</label>
              <select
                id="version-select"
                value={currentVersion}
                onChange={(e) => handleVersionChange(e.target.value)}
                className="version-dropdown"
              >
                {VISIBLE_VERSIONS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <Divider />
            <RouterNavLink href={`/api/${currentSlug}/welcome`} label="Welcome" />
            <Divider label="REST API" labelPosition="left" />

            <RouterNavLink href={`/api/${currentSlug}/authentication`} label="Authentication and Certificates" />
            {usesSplitOpenApiSpecs(currentVersion) ? (
              <RouterNavLink
                href={`/api/${currentSlug}/api-reference`}
                label="REST API Reference"
                childrenOffset={28}
                defaultOpened
                end
              >
                {(calculators ?? []).map((calculator) => (
                  <RouterNavLink
                    key={calculator}
                    href={`/api/${currentSlug}/api-reference/${calculator}`}
                    label={calculator}
                  />
                ))}
              </RouterNavLink>
            ) : (
              <RouterNavLink href={`/api/${currentSlug}/api-reference`} label="REST API Reference" />
            )}

            {hasNpmPackage(currentVersion) && (
              <>
                <Divider label="NPM Package" labelPosition="left" />
                <RouterNavLink href={`/api/${currentSlug}/package`} label="Using the NPM package" />
                <RouterNavLink href={`/api/${currentSlug}/sdk`} label="Package API reference" />
              </>
            )}
            <Divider label="Advanced" labelPosition="left" />
            <RouterNavLink href={`/api/${currentSlug}/constants`} label="Constants" />
            {hasPreV4Pages(currentVersion) && (
              <>
                <RouterNavLink href={`/api/${currentSlug}/vegetation`} label="Vegetation inputs" />
                <RouterNavLink href={`/api/${currentSlug}/outputs`} label="Outputs" />
              </>
            )}
          </Flex>
        </ScrollArea>
      </AppShell.Navbar>
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
      <AppShell.Footer zIndex={200}>
        <Flex h="100%">
          <Flex miw="300px" w="300px" className="footer-copyright" justify="flex-start" align="center" m="sm">
            <p>&copy; 2025 Agricultural Innovation Australia. All rights reserved.</p>
          </Flex>
          <Grid w="100%" h="100%" m="0" classNames={{ inner: 'grid-inner' }}>
            <Grid.Col span={9} m={0} py={0}>
              <Flex justify="flex-end" align="center" h="100%">
                <Flex align="center" gap="xs">
                  <span>Made with ❤️ by</span>
                  <Link to="https://exogee.com">
                    <img src={isDarkMode ? exogeeWhite : exogeeBlack} alt="Exogee Logo" className="exogee-logo" />
                  </Link>
                </Flex>
              </Flex>
            </Grid.Col>
          </Grid>
        </Flex>
      </AppShell.Footer>
    </AppShell>
  );
}

export default Layout;
